// WHAT: Tab component for displaying overview of worked vs planned time
// WHY:  Provides comparison view of actual vs planned time by workspace or aspect
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: BarChart component, OverviewEntry type
// RISKS:
//   - NOTE: Progress calculation may not handle edge cases (negative values, division by zero)
//   - NOTE: Color mapping may not match workspace config colors
//   - NOTE: Time filter toggle may not update data correctly
//   - NOTE: Loading state logic may show incorrect state

import type { FC } from "react";

import { BarChart3, Users, Target, TrendingUp, Clock, CheckCircle } from "lucide-react";

import { Button, Card, BarChart } from "@/components/ui";

/**
 * Overview entry interface
 */
interface OverviewEntry {
  /** Display color */
  color: string;
  /** Entry name (workspace or aspect) */
  name: string;
  /** Planned hours */
  planned: number;
  /** Worked hours */
  worked: number;
}

/**
 * WHAT:
 *   Gets status badge class based on progress percentage.
 *
 * @param progress - Progress percentage (0-100)
 *
 * @returns CSS class string for status badge
 *
 */
const getStatusBadgeClass = (progress: number): string => {
  if (progress >= 100) {
    return "border-green-500/30 text-green-400 bg-green-500/10";
  }
  if (progress >= 80) {
    return "border-tracq-bright-blue/30 text-tracq-bright-blue bg-tracq-bright-blue/10";
  }
  if (progress >= 60) {
    return "border-yellow-500/30 text-yellow-400 bg-yellow-500/10";
  }
  return "border-red-500/30 text-red-400 bg-red-500/10";
};

/**
 * WHAT:
 *   Gets status badge text based on progress percentage.
 *
 * @param progress - Progress percentage (0-100)
 *
 * @returns Status badge text
 *
 */
const getStatusBadgeText = (progress: number): string => {
  if (progress >= 100) {
    return "ERREICHT";
  }
  if (progress >= 80) {
    return "IM PLAN";
  }
  if (progress >= 60) {
    return "RÜCKSTAND";
  }
  return "KRITISCH";
};

/**
 * WHAT:
 *   Gets status description text based on progress percentage.
 *
 * @param progress - Progress percentage (0-100)
 *
 * @returns Status description text
 *
 */
const getStatusDescription = (progress: number): string => {
  if (progress >= 100) {
    return "Ziel erreicht";
  }
  if (progress >= 80) {
    return "Im Plan";
  }
  if (progress >= 60) {
    return "Leicht im Rückstand";
  }
  return "Kritischer Rückstand";
};

/**
 * WHAT:
 *   Gets status text color class based on progress percentage.
 *
 * @param progress - Progress percentage (0-100)
 *
 * @returns CSS class string for text color
 *
 */
const getStatusTextColor = (progress: number): string => {
  if (progress >= 100) {
    return "text-green-400";
  }
  if (progress >= 80) {
    return "text-tracq-bright-blue";
  }
  if (progress >= 60) {
    return "text-yellow-400";
  }
  return "text-red-400";
};

/**
 * WHAT:
 *   Renders statistics header card with overview metrics.
 *
 * @param props - Component props
 *
 * @param props.currentData - Current overview data
 *
 * @param props.overallProgress - Overall progress percentage
 *
 * @param props.overviewMode - Current overview mode
 *
 * @param props.setOverviewMode - Mode change handler
 *
 * @param props.setTimeFilter - Optional time filter change handler
 *
 * @param props.timeFilter - Current time filter
 *
 * @returns Statistics header card component
 *
 */
const OverviewHeader: FC<{
  currentData: OverviewEntry[];
  overallProgress: number;
  overviewMode: "aspect" | "workspace";
  setOverviewMode: (mode: "aspect" | "workspace") => void;
  setTimeFilter?: (filter: "monthly" | "today") => void;
  timeFilter?: "monthly" | "today";
}> = ({
  currentData,
  overallProgress,
  overviewMode,
  setOverviewMode,
  setTimeFilter,
  timeFilter = "monthly",
}) => {
  const totalWorked = currentData.reduce((sum, entry) => sum + entry.worked, 0);
  const totalPlanned = currentData.reduce((sum, entry) => sum + entry.planned, 0);

  return (
    <Card
      className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div className="space-y-6">
        {/* Titel und Info */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-tracq-bright-blue/20 flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-white">Übersicht</h3>
            <p className="text-slate-400">
              {overviewMode === "workspace"
                ? "Fortschritt nach Workspaces"
                : "Fortschritt nach Aspekten"}
            </p>
          </div>
        </div>

        {/* Statistiken Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-tracq-bright-blue/20 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-tracq-bright-blue" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Gesamt-Fortschritt</div>
                <div className="text-xl font-bold text-tracq-bright-blue">
                  {overallProgress.toFixed(0)}%
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-500/20 flex items-center justify-center">
                <Users className="w-4 h-4 text-slate-300" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Bereiche</div>
                <div className="text-xl font-bold text-white">{currentData.length}</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Gearbeitet</div>
                <div className="text-xl font-bold text-green-400">{totalWorked.toFixed(1)}h</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-600/30 rounded-xl p-4 border border-slate-500/30">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-500/20 flex items-center justify-center">
                <Target className="w-4 h-4 text-slate-300" />
              </div>
              <div>
                <div className="text-sm text-slate-400">Geplant</div>
                <div className="text-xl font-bold text-white">{totalPlanned.toFixed(1)}h</div>
              </div>
            </div>
          </div>
        </div>

        {/* Mode Toggle - Integriert in Header */}
        <div className="flex gap-3 pt-4 border-t border-slate-600/50">
          <Button
            className="flex-1"
            onClick={() => {
              setOverviewMode("workspace");
            }}
            size="sm"
            variant={overviewMode === "workspace" ? "primary" : "secondary"}
          >
            <Users className="w-4 h-4 mr-2" />
            Workspaces
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              setOverviewMode("aspect");
            }}
            size="sm"
            variant={overviewMode === "aspect" ? "primary" : "secondary"}
          >
            <Target className="w-4 h-4 mr-2" />
            Aspects
          </Button>
        </div>

        {/* Zeit-Filter - Falls verfügbar */}
        {setTimeFilter ? (
          <div className="flex gap-3 pt-2 border-t border-slate-600/30">
            <Button
              className="flex-1"
              onClick={() => {
                setTimeFilter("today");
              }}
              size="sm"
              variant={timeFilter === "today" ? "primary" : "secondary"}
            >
              <Clock className="w-4 h-4 mr-2" />
              Bis heute
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                setTimeFilter("monthly");
              }}
              size="sm"
              variant={timeFilter === "monthly" ? "primary" : "secondary"}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Monatsziel
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
};

/**
 * Props for OverviewTab component
 */
interface Properties {
  /** Aspect overview data */
  aspectOverview: OverviewEntry[];
  /** Data loading state */
  dataLoading?: boolean;
  /** Overview mode (workspace or aspect) */
  overviewMode: "aspect" | "workspace";
  /** Set overview mode handler */
  setOverviewMode: (mode: "aspect" | "workspace") => void;
  /** Set time filter handler */
  setTimeFilter?: (filter: "monthly" | "today") => void;
  /** Time filter (today or monthly) */
  timeFilter?: "monthly" | "today";
  /** Workspace overview data */
  workspaceOverview: OverviewEntry[];
}

/**
 * WHAT:
 *   Tab component that displays overview of worked vs planned time with
 *   workspace or aspect breakdown and time filter options.
 *
 * WHY:
 *   Enables users to compare actual time spent against planned time to
 *   identify shortfalls and overages.
 *
 * PROPS:
 *   - overviewMode: 'workspace' | 'aspect' — Display mode
 *   - setOverviewMode: (mode) => void — Mode change handler
 *   - workspaceOverview: OverviewEntry[] — Workspace data
 *   - aspectOverview: OverviewEntry[] — Aspect data
 *   - dataLoading?: boolean — Loading state
 *   - timeFilter?: 'today' | 'monthly' — Time filter
 *   - setTimeFilter?: (filter) => void — Filter change handler
 *
 * BEHAVIOR:
 *   - Displays bar chart comparing worked vs planned
 *   - Supports workspace or aspect view
 *   - Shows progress indicators
 *   - Supports today/monthly filter
 *   - Displays summary statistics
 *
 * RISKS:
 *   - NOTE: Progress calculation may not handle edge cases (negative values, division by zero)
 *   - NOTE: Color mapping may not match workspace config colors
 *   - NOTE: Time filter toggle may not update data correctly
 *   - NOTE: Loading state logic may show incorrect state
 *
 * @param root0
 *
 * @param root0.overviewMode
 *
 * @param root0.setOverviewMode
 *
 * @param root0.workspaceOverview
 *
 * @param root0.aspectOverview
 *
 * @param root0.dataLoading
 *
 * @param root0.timeFilter
 *
 * @param root0.setTimeFilter
 *
 * @returns Overview tab component with worked vs planned time comparison
 *
 */
export const OverviewTab: FC<Properties> = ({
  aspectOverview,
  dataLoading = false,
  overviewMode,
  setOverviewMode,
  setTimeFilter,
  timeFilter = "monthly",
  workspaceOverview,
}) => {
  // NOTE: Show loading state only if data is actively loading AND we have no data
  const hasData = (workspaceOverview.length || 0) > 0 || (aspectOverview.length || 0) > 0;

  if (dataLoading && !hasData) {
    return (
      <Card
        className="h-full flex items-center justify-center bg-slate-700/30 border-slate-600/50"
        padding="lg"
        variant="elevated"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tracq-bright-blue mx-auto mb-4" />
          <p className="text-slate-400">Lade Übersicht...</p>
        </div>
      </Card>
    );
  }

  const currentData = overviewMode === "workspace" ? workspaceOverview : aspectOverview;

  // Show empty state if no data
  if (currentData.length === 0) {
    return (
      <Card
        className="h-full flex items-center justify-center bg-slate-700/30 border-slate-600/50"
        padding="lg"
        variant="elevated"
      >
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">Keine Daten verfügbar</p>
          <p className="text-slate-500 text-sm">
            Für {overviewMode === "workspace" ? "Workspaces" : "Aspekte"}
          </p>
        </div>
      </Card>
    );
  }

  const totalWorked = currentData.reduce((sum, entry) => sum + entry.worked, 0);
  const totalPlanned = currentData.reduce((sum, entry) => sum + entry.planned, 0);
  const overallProgress = totalPlanned > 0 ? (totalWorked / totalPlanned) * 100 : 0;

  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header mit Statistiken - Modernisiertes Layout */}
      <OverviewHeader
        currentData={currentData}
        overallProgress={overallProgress}
        overviewMode={overviewMode}
        setOverviewMode={setOverviewMode}
        {...(setTimeFilter ? { setTimeFilter } : {})}
        timeFilter={timeFilter}
      />

      {/* Chart Section */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card hover:border-slate-500/50 transition-all duration-200"
        padding="lg"
        variant="elevated"
      >
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-lg font-semibold text-white">
            {overviewMode === "workspace" ? "Workspace Performance" : "Aspect Performance"}
          </h4>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <div className="w-3 h-3 bg-tracq-bright-blue rounded" />
            <span>Gearbeitet</span>
            <div className="w-3 h-3 bg-slate-500 rounded ml-4" />
            <span>Geplant</span>
          </div>
        </div>

        <BarChart
          data={currentData.map((d) => ({
            color: d.color,
            label: d.name,
            planned: d.planned,
            worked: d.worked,
          }))}
          xKey="label"
          yKey="worked"
        />
      </Card>

      {/* Detailed Stats */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid gap-3">
          {currentData.map((entry) => {
            const progress = entry.planned > 0 ? (entry.worked / entry.planned) * 100 : 0;
            const shortfall = Math.max(0, entry.planned - entry.worked);

            return (
              <Card
                className="group bg-slate-800/30 hover:bg-slate-800/40 transition-all duration-200 border-slate-600/40 hover:border-slate-500/60 hover:scale-[1.01]"
                key={entry.name}
                variant="default"
              >
                <div className="p-4">
                  {/* Header mit Status-Indikator */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {/* Status-Indikator als farbiger Punkt */}
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-semibold text-white">{entry.name}</h4>
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs font-medium border ${getStatusBadgeClass(progress)}`}
                          >
                            {getStatusBadgeText(progress)}
                          </span>
                        </div>
                        <p className={`text-sm ${getStatusTextColor(progress)}`}>
                          {getStatusDescription(progress)}
                        </p>
                      </div>
                    </div>

                    {/* Fortschritt */}
                    <div className="text-right">
                      <div className="text-xl font-bold text-white">{progress.toFixed(0)}%</div>
                      <div className="text-xs text-slate-500">Fortschritt</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          backgroundColor: entry.color,
                          width: `${String(Math.min(progress, 100))}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Detailstatistiken */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Gearbeitet</div>
                      <div className="font-semibold text-white">{entry.worked.toFixed(1)}h</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">Geplant</div>
                      <div className="font-semibold text-white">{entry.planned.toFixed(1)}h</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-400 mb-1">
                        {shortfall > 0 ? "Rückstand" : "Überschuss"}
                      </div>
                      <div
                        className={`font-semibold ${shortfall > 0 ? "text-red-400" : "text-green-400"}`}
                      >
                        {shortfall > 0
                          ? `-${shortfall.toFixed(1)}h`
                          : `+${(entry.worked - entry.planned).toFixed(1)}h`}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};
